import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import FormField from './FormField';
import css from './ExpandedDimension.css';
import ValueSourceSection from './ValueSourceSection';
import { Button, TextField } from '../../components/widgets';
import ToggleTabGroup from '../../components/atoms/ToggleTabGroup';
import AutoCompleteField from '../../components/molecules/AutoCompleteField';
import sharedCss from './styles.css';
import { improveDescription, improveOptions } from '../services';
import toaster from '../../utils/toaster';
import { trackMixpanelEvent } from '../../utils/general';
import {
    buildMixpanelPayload,
    CustomAIDimensionContext,
    FORM_FIELDS,
    FORM_TRANSLATIONS,
    getValueTypeOptions,
    MIXPANEL,
    PLAYGROUND_TRANSLATIONS,
    POPUP_DELAY,
    TOASTER_TRANSLATIONS,
    VALUE_TYPES,
} from '../consts';
import { useImprovePrompt } from '../hooks/useImprovePrompt';
import ImproveActions from './ImproveActions';
import { DimensionPlaygroundPopup } from './DimensionPlayground';

const APPS_LABEL = 'STATIC.PLACEHOLDERS.SELECT_APP';
const DESCRIPTION_CHAR_LIMIT = 2048;

const sortedStringify = arr => JSON.stringify([...(arr || [])].sort());

const hasFormChanged = (formData, initialData) =>
    formData.displayName !== initialData.displayName ||
    formData.description !== initialData.description ||
    formData.valueType !== initialData.valueType ||
    sortedStringify(formData.apps) !== sortedStringify(initialData.apps) ||
    sortedStringify(formData.options) !== sortedStringify(initialData.options);

const getFormValidation = (formData, hasChanges, isNewDimension) => {
    const isDescriptionLengthValid =
        formData.description?.trim() && formData.description.length <= DESCRIPTION_CHAR_LIMIT;
    const isAppsEmpty = formData.apps !== null && formData.apps.length === 0;
    const isDisplayNameEmpty = !formData.displayName?.trim();

    const canSave = isDescriptionLengthValid && !isAppsEmpty && !isDisplayNameEmpty && (isNewDimension || hasChanges);

    return canSave;
};

const ExpandedDimension = ({
    customAIDimension,
    originalDisplayName,
    isNewDimension,
    onUpdate,
    onCancel,
    onHasChangesChange,
    translate,
}) => {
    const { isReadOnly, apps, isAppsLoading, doesDisplayNameExist } = useContext(CustomAIDimensionContext);

    const dimensionId = isNewDimension ? null : customAIDimension.id;

    const valueTypeOptions = getValueTypeOptions(isReadOnly);

    const initialDataRef = useRef({
        displayName: originalDisplayName,
        description: customAIDimension.description,
        valueType: customAIDimension.value_type ?? VALUE_TYPES.SINGLE,
        options: customAIDimension.options || [],
        apps: customAIDimension.apps?.length ? customAIDimension.apps : null,
    });

    const [formData, setFormData] = useState(initialDataRef.current);
    const [showPlayground, setShowPlayground] = useState(false);

    const buildDimensionPayload = useCallback(() => {
        const payload = {
            display_name: formData.displayName.trim(),
            description: formData.description.trim(),
            options: formData.options,
            apps: formData.apps,
            value_type: formData.valueType,
        };
        if (dimensionId) payload.id = dimensionId;
        return payload;
    }, [formData, dimensionId]);

    useEffect(() => {
        setFormData(prev => ({ ...prev, displayName: customAIDimension.display_name || '' }));
    }, [customAIDimension.display_name]);

    const initialData = initialDataRef.current;
    const selectedAppsOptions = formData.apps === null ? apps : apps.filter(app => formData.apps?.includes(app.value));
    const selectedValueTypeOption =
        valueTypeOptions.find(option => option.value === formData.valueType)?.option || valueTypeOptions[0].option;

    const hasChanges = useMemo(() => hasFormChanged(formData, initialData), [formData, initialData]);

    useEffect(() => {
        onHasChangesChange(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    const canSave = useMemo(() => getFormValidation(formData, hasChanges, isNewDimension), [
        formData,
        hasChanges,
        isNewDimension,
    ]);

    const canTestDimension = useMemo(() => {
        const hasName = formData.displayName?.trim().length > 0;
        const hasDescription = formData.description?.trim().length > 0;
        const hasApps = formData.apps === null || formData.apps?.length > 0;
        return hasName && hasDescription && hasApps;
    }, [formData.displayName, formData.description, formData.apps]);

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const hasRequiredFields = !!formData.displayName?.trim() && !!formData.description?.trim();

    const sharedImproveProps = {
        hasRequiredFields,
        dimensionDisplayName: formData.displayName,
        isReadOnly,
        translate,
    };

    const descriptionImproving = useImprovePrompt({
        ...sharedImproveProps,
        formContent: formData.description,
        improveFetcher: improveDescription,
        onContentChange: value => handleFieldChange(FORM_FIELDS.DESCRIPTION, value),
    });

    const optionsImproving = useImprovePrompt({
        ...sharedImproveProps,
        formContent: formData.options,
        improveFetcher: (options, displayName) => improveOptions(options, displayName, formData.description),
        onContentChange: value => handleFieldChange(FORM_FIELDS.OPTIONS, value),
    });

    const clearImproveSnapshots = () => {
        descriptionImproving.clearImproveSnapshot();
        optionsImproving.clearImproveSnapshot();
    };

    const handleValueTypeToggle = option => {
        const selectedOption = valueTypeOptions.find(opt => opt.option === option);
        handleFieldChange(FORM_FIELDS.VALUE_TYPE, selectedOption?.value ?? VALUE_TYPES.SINGLE);
    };

    const handleAppsChange = selectedApps => {
        const appIds = selectedApps?.map(app => app.value);
        handleFieldChange(FORM_FIELDS.APPS, appIds.length === apps.length ? null : appIds);
    };

    const handleCancelEditing = () => {
        if (!isNewDimension) setFormData(initialData);

        clearImproveSnapshots();
        onCancel();
    };

    const trackDimensionEvent = (action, payload) => {
        trackMixpanelEvent(MIXPANEL.EVENT_PREFIX, action, buildMixpanelPayload(payload, apps));
    };

    const handleTestDimension = () => {
        const payload = buildDimensionPayload();
        trackDimensionEvent(MIXPANEL.ACTIONS.TEST_DIMENSION, payload);
        setShowPlayground(true);
    };

    const handleSave = () => {
        const payload = buildDimensionPayload();

        const isNewName = payload.display_name.toLowerCase() !== originalDisplayName.trim().toLowerCase();

        if (isNewName && doesDisplayNameExist(payload.display_name)) {
            toaster.error(translate(TOASTER_TRANSLATIONS.DUPLICATE_NAME_ERROR), { autoClose: POPUP_DELAY });
            return;
        }

        const action = isNewDimension ? MIXPANEL.ACTIONS.SAVE_NEW : MIXPANEL.ACTIONS.EDIT;
        trackDimensionEvent(action, payload);
        onUpdate({ payload, originalDisplayName, onSuccess: clearImproveSnapshots });
    };

    return (
        <div className={css.container}>
            <div className={css.section}>
                <span className={css.sectionTitle}>Configuration</span>
                <FormField
                    title={FORM_TRANSLATIONS.DESCRIPTION_TITLE}
                    required
                    subTitle={FORM_TRANSLATIONS.DESCRIPTION_SUBTITLE}
                >
                    <div className={css.description}>
                        <TextField
                            value={formData.description}
                            onChange={descriptionImproving.handleContentChange}
                            type="textarea"
                            placeholder={FORM_TRANSLATIONS.DESCRIPTION_PLACEHOLDER}
                            inputConfig={{ rows: 4 }}
                            inputClassName={sharedCss.textAreaField}
                            showCharCount
                            containerClass={css.descriptionTextArea}
                            maxLengthForCounter={DESCRIPTION_CHAR_LIMIT}
                            disabled={isReadOnly || descriptionImproving.isImproving}
                        />
                        {!isReadOnly && (
                            <ImproveActions
                                isImproving={descriptionImproving.isImproving}
                                canRevert={descriptionImproving.canRevert}
                                shouldDisableImprove={
                                    descriptionImproving.shouldDisableImprove || optionsImproving.isImproving
                                }
                                onImprove={descriptionImproving.onImprove}
                                onRevert={descriptionImproving.onRevert}
                            />
                        )}
                    </div>
                </FormField>
                <FormField
                    title={FORM_TRANSLATIONS.RESULT_TYPE_TITLE}
                    tooltip={FORM_TRANSLATIONS.RESULT_TYPE_TOOLTIP}
                    subTitle={FORM_TRANSLATIONS.RESULT_TYPE_SUBTITLE}
                >
                    <ToggleTabGroup
                        options={valueTypeOptions}
                        selectedOption={selectedValueTypeOption}
                        onToggle={handleValueTypeToggle}
                    />
                </FormField>
                <FormField title={FORM_TRANSLATIONS.APPS_TITLE} required subTitle={FORM_TRANSLATIONS.APPS_SUBTITLE}>
                    <div className={css.appsContainer}>
                        <AutoCompleteField
                            options={apps}
                            disableOptions={isReadOnly}
                            value={selectedAppsOptions}
                            onChange={handleAppsChange}
                            placeholder={FORM_TRANSLATIONS.APPS_PLACEHOLDER}
                            label={APPS_LABEL}
                            isMulti
                            advanced
                            loading={isAppsLoading}
                        />
                    </div>
                </FormField>
            </div>

            <div className={css.section}>
                <ValueSourceSection
                    values={formData.options}
                    onChange={optionsImproving.handleContentChange}
                    isImproving={optionsImproving.isImproving}
                    onImprove={optionsImproving.onImprove}
                    onRevert={optionsImproving.onRevert}
                    canRevert={optionsImproving.canRevert}
                    shouldDisableImprove={optionsImproving.shouldDisableImprove || descriptionImproving.isImproving}
                />
                <div className={css.updateDimensionActions}>
                    <Button text={FORM_TRANSLATIONS.CANCEL_BUTTON} type="secondary" onClick={handleCancelEditing} />
                    {!isReadOnly && (
                        <>
                            <Button
                                text={PLAYGROUND_TRANSLATIONS.TEST_BUTTON}
                                type="secondary"
                                onClick={handleTestDimension}
                                disabled={!canTestDimension}
                                Icon="sparkles"
                            />
                            <Button
                                text={
                                    isNewDimension ? FORM_TRANSLATIONS.CREATE_BUTTON : FORM_TRANSLATIONS.UPDATE_BUTTON
                                }
                                type="primary"
                                onClick={handleSave}
                                disabled={!canSave}
                            />
                        </>
                    )}
                </div>
            </div>
            <DimensionPlaygroundPopup
                open={showPlayground}
                onClose={() => setShowPlayground(false)}
                dimensionConfig={buildDimensionPayload()}
            />
        </div>
    );
};

ExpandedDimension.propTypes = {
    customAIDimension: PropTypes.shape({
        id: PropTypes.string,
        apps: PropTypes.arrayOf(PropTypes.number),
        description: PropTypes.string,
        display_name: PropTypes.string,
        value_type: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    originalDisplayName: PropTypes.string,
    isNewDimension: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onHasChangesChange: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(ExpandedDimension);
